import { API_BASE } from './config'
import { getAuthHeaders } from './api'
import type { PlanResult } from '@/lib/studyPlan/engine'
import type { TopicProgressV2 } from '@/lib/studyPlan/v2/types'

export interface StoredPlanConfig {
  grade: string
  area?: string
  goal?: string | null
  hours: string
  dailyMinutes?: number
  kelime?: boolean
  dayStart?: string | null
  regeneratedWeekStart?: string | null
}

export interface StudyPlanCompletion {
  date: string
  day_idx: number
  block_idx: number
  mins: number
}

export interface PlanResultV2 {
  engine: string
  countdown: number
  focus: string
  phaseLabel: string
  areaLabel: string
  week: unknown
  stats: {
    totalMins: number
    coreMins: number
    video: number
    soru: number
    deneme: number
    tekrar: number
  }
}

export interface StudyPlanRecord {
  config: StoredPlanConfig | null
  plan_json: PlanResult | PlanResultV2 | null
  engine_version: string | null
  status: string
}

export interface StudyPlanResponse {
  plan: StudyPlanRecord
  completions: StudyPlanCompletion[]
}

export interface ChildPlanResponse {
  config: StoredPlanConfig | null
  plan_json: PlanResult | PlanResultV2 | null
  status: string
  completions: StudyPlanCompletion[]
}

export interface DayLogEntry {
  date: string
  template_id: string | null
  energy: number | null
  checked_in: number
  blocks_done: number
  blocks_planned: number
  min_day: number
  core_done: number
  note: string | null
}

export interface DenemeResultEntry {
  date: string
  kind: string
  nets: Record<string, number> | null
}

export interface DayLogPutBody {
  date: string
  energy?: number
  template_id?: string
  checked_in?: number
  blocks_done?: number
  blocks_planned?: number
  min_day?: number
  core_done?: number
}

export interface DenemeResultBody {
  date: string
  kind: 'tyt' | 'ayt' | 'brans'
  nets: Record<string, number>
}

export type TopicProgressRow = TopicProgressV2

export const CHILD_PLAN_FORBIDDEN = 'CHILD_PLAN_FORBIDDEN'

interface ApiEnvelope<T> {
  success: boolean
  data?: T
  error?: { code: string; message: string }
}

async function request<T>(path: string, method: string, body?: unknown): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: body === undefined ? undefined : JSON.stringify(body)
  })

  const result = await response.json().catch(() => null) as ApiEnvelope<T> | null

  if (!response.ok) {
    throw new Error(result?.error?.message || 'İşlem başarısız oldu')
  }
  if (!result?.success || result.data === undefined) {
    throw new Error(result?.error?.message || 'İşlem başarısız oldu')
  }
  return result.data
}

export const studyPlanService = {
  getPlan: async (): Promise<StudyPlanResponse | null> => {
    const response = await fetch(`${API_BASE}/study-plan`, {
      headers: getAuthHeaders()
    })
    if (response.status === 404) {
      return null
    }
    const result = await response.json().catch(() => null) as ApiEnvelope<StudyPlanResponse> | null
    if (!response.ok || !result?.success || !result.data) {
      throw new Error(result?.error?.message || 'Çalışma planı yüklenemedi')
    }
    return result.data
  },

  getChildPlan: async (childId: number): Promise<ChildPlanResponse | null> => {
    const response = await fetch(`${API_BASE}/study-plan?child_id=${childId}`, {
      headers: getAuthHeaders()
    })
    if (response.status === 404) {
      return null
    }
    const result = await response.json().catch(() => null) as ApiEnvelope<ChildPlanResponse> | null
    if (response.status === 403 || result?.error?.code === 'FORBIDDEN') {
      throw new Error(CHILD_PLAN_FORBIDDEN)
    }
    if (!response.ok || !result?.success || !result.data) {
      throw new Error(result?.error?.message || 'Çalışma planı yüklenemedi')
    }
    return result.data
  },

  savePlan: (config: StoredPlanConfig, planJson: PlanResult | PlanResultV2, engineVersion: string) =>
    request<{ plan: StudyPlanRecord }>('/study-plan', 'PUT', {
      config,
      plan_json: planJson,
      engine_version: engineVersion
    }),

  updateStatus: (status: 'active' | 'paused') =>
    request<{ plan: { status: string } }>('/study-plan/status', 'PATCH', { status }),

  saveCompletions: (items: StudyPlanCompletion[]) =>
    request<{ saved: number; completions: StudyPlanCompletion[] }>('/study-plan/completions', 'POST', items),

  putDayLog: (body: DayLogPutBody) =>
    request<{ entry: DayLogEntry; streak: { current_streak: number } | null; core_points: number }>(
      '/study-plan/day-log',
      'PUT',
      body
    ),

  getDayLogs: (limit = 30) =>
    request<{ entries: DayLogEntry[] }>(`/study-plan/day-log?limit=${limit}`, 'GET'),

  getDenemeResults: (params: { date?: string; kind?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams()
    if (params.date) qs.set('date', params.date)
    if (params.kind) qs.set('kind', params.kind)
    qs.set('limit', String(params.limit ?? 50))
    return request<{ entries: DenemeResultEntry[] }>(`/study-plan/deneme-results?${qs.toString()}`, 'GET')
  },

  postDenemeResult: (body: DenemeResultBody) =>
    request<{ entry: DenemeResultEntry; points_awarded: number }>('/study-plan/deneme-results', 'POST', body),

  getTopicsProgress: (limit = 500) =>
    request<{ entries: TopicProgressRow[] }>(`/study-plan/topics-progress?limit=${limit}`, 'GET'),

  postTopicsProgress: (rows: Partial<TopicProgressRow>[] ) =>
    request<{ saved: number; entries: TopicProgressRow[] }>('/study-plan/topics-progress', 'POST', rows)
}
